import { z } from 'zod'
import { Configuration } from './schema.ts'

/**
 * Le schéma des outils du modèle, dérivé du schéma Zod pour qu'il ne puisse
 * jamais diverger de la grammaire. Deux contraintes de l'API imposent la forme :
 * `additionalProperties` doit valoir false partout, et aucune récursion n'est
 * acceptée. Les conditions, qui sont récursives par nature, sont donc déroulées
 * à profondeur bornée : trois niveaux d'imbrication, feuilles au quatrième.
 *
 * Le schéma déroulé est permissif au dernier niveau, jamais l'inverse : il ne
 * peut pas accepter un document que Zod refuserait, il peut refuser une
 * condition à quatre niveaux que Zod accepterait. Le serveur repasse de toute
 * façon chaque document par `validerDocument`.
 */

export type SchemaJson = Record<string, unknown>

/** Trois niveaux d'imbrication de `tout`, `un_de` et `non`, feuilles au quatrième. */
export const PROFONDEUR_CONDITIONS = 3

/**
 * Ce que coûte la profondeur, mesuré sur la grammaire actuelle : le document
 * cite une condition à vingt-six endroits, et chaque niveau la triple. Zéro
 * niveau pèse 50 ko, un niveau 155 ko, deux niveaux 471 ko, trois niveaux
 * 1 418 ko. Aucune API n'accepte un outil de 1,4 Mo, et la facture en jetons
 * serait payée à chaque tour de conversation : le serveur qui tient la boucle
 * passe cette profondeur-ci, et garde la profondeur complète pour valider un
 * document importé hors conversation.
 */
export const PROFONDEUR_CONVERSATION = 1

const MOTS_CLES_OBJET = new Set(['properties', 'required', 'additionalProperties'])

/**
 * Inline chaque `$ref` avec un budget de profondeur par définition. Quand le
 * budget est épuisé, la branche récursive disparaît : une union perd ses
 * combinateurs et ne garde que ses feuilles, un objet dont une propriété
 * obligatoire a disparu s'efface à son tour.
 */
export function derouler(schema: SchemaJson, profondeur = PROFONDEUR_CONDITIONS): SchemaJson {
  const definitions = (schema.$defs ?? {}) as Record<string, SchemaJson>

  function visiter(noeud: unknown, budgets: Map<string, number>): unknown {
    if (Array.isArray(noeud)) return noeud.map((n) => visiter(n, budgets))
    if (noeud === null || typeof noeud !== 'object') return noeud
    const source = noeud as SchemaJson

    if (typeof source.$ref === 'string') {
      const nom = source.$ref.split('/').pop() ?? ''
      const definition = definitions[nom]
      if (!definition) return null
      // Un niveau de plus que la profondeur demandée : le dernier ne porte que des feuilles.
      const reste = budgets.get(nom) ?? profondeur + 1
      if (reste <= 0) return null
      const suivants = new Map(budgets)
      suivants.set(nom, reste - 1)
      return visiter(definition, suivants)
    }

    const sortie: SchemaJson = {}
    const requis = Array.isArray(source.required) ? (source.required as string[]) : []
    for (const [cle, valeur] of Object.entries(source)) {
      if (cle === '$defs' || cle === '$schema' || cle === '$id') continue
      // `oneOf` et `anyOf` disent la même chose sur nos unions discriminées, et
      // un seul mot-clé simplifie tout ce qui lit ce schéma ensuite.
      if (cle === 'anyOf' || cle === 'oneOf') {
        const branches = (valeur as unknown[]).map((b) => visiter(b, budgets)).filter((b) => b !== null)
        if (!branches.length) return null
        sortie.anyOf = branches
        continue
      }
      if (cle === 'properties') {
        const proprietes: SchemaJson = {}
        for (const [nom, sous] of Object.entries(valeur as SchemaJson)) {
          const resultat = visiter(sous, budgets)
          if (resultat === null) {
            if (requis.includes(nom)) return null
            continue
          }
          proprietes[nom] = resultat
        }
        sortie.properties = proprietes
        continue
      }
      if (cle === 'items' || cle === 'not') {
        const resultat = visiter(valeur, budgets)
        if (resultat === null) return null
        sortie[cle] = resultat
        continue
      }
      if (MOTS_CLES_OBJET.has(cle) || typeof valeur !== 'object' || valeur === null) {
        sortie[cle] = valeur
        continue
      }
      sortie[cle] = visiter(valeur, budgets)
    }
    if (sortie.type === 'object' || sortie.properties) {
      sortie.type = 'object'
      sortie.additionalProperties = false
    }
    return sortie
  }

  const deroule = visiter(schema, new Map())
  if (deroule === null || typeof deroule !== 'object') throw new Error('Le schéma s’est effondré au déroulement')
  return deroule as SchemaJson
}

function schemaZod(schema: z.ZodType, profondeur: number): SchemaJson {
  const brut = z.toJSONSchema(schema, { io: 'input', target: 'draft-2020-12', unrepresentable: 'any', cycles: 'ref' }) as SchemaJson
  return derouler(brut, profondeur)
}

/** Le document de configuration, tel que le modèle a le droit de l'écrire. */
export function schemaDocument(profondeur = PROFONDEUR_CONDITIONS): SchemaJson {
  return schemaZod(Configuration, profondeur)
}

const Question = z
  .object({
    /** Où la réponse ira dans le document : le responsable ne voit jamais ce chemin. */
    chemin: z.string().min(1),
    question: z.string().min(1),
    pourquoi: z.string().min(1),
    /** Pré-rempli dans la carte : le responsable n'a qu'à confirmer. */
    defaut: z.string().optional(),
    choix: z.array(z.string().min(1)).max(6).default([]),
  })
  .strict()

const PoserQuestions = z.object({ questions: z.array(Question).min(1).max(5) }).strict()
const Publier = z.object({ citation: z.string().min(1) }).strict()

export interface OutilDuModele {
  name: string
  description: string
  input_schema: SchemaJson
}

/**
 * Les trois outils qui touchent à la grammaire. Les outils de lecture et les
 * actions du quotidien appartiennent au serveur : ils ont besoin de la base,
 * pas du schéma de configuration.
 */
export function outilsDuModele(profondeur = PROFONDEUR_CONDITIONS): OutilDuModele[] {
  return [
    {
      name: 'proposer_configuration',
      description:
        'Propose le document de configuration complet d’un service. Écrit uniquement un brouillon : rien n’est publié, aucune réservation n’est touchée. Retourne les manques, les hypothèses appliquées, le diff avec la version en ligne et son impact sur la disponibilité. Réécris le document en entier à chaque appel.',
      input_schema: schemaZod(Configuration, profondeur),
    },
    {
      name: 'poser_questions',
      description:
        'Pose au responsable les questions qui restent, cinq au maximum pour tout le service. Chaque question porte un défaut déjà appliqué : le responsable confirme ou corrige. N’en pose aucune sur ce que la fiche de l’organisation contient déjà.',
      input_schema: schemaZod(PoserQuestions, profondeur),
    },
    {
      name: 'publier',
      description:
        'Met la page en ligne. Accepté seulement si un aperçu existe depuis la dernière modification, si la validation est propre, et si la citation reprend mot pour mot un extrait du dernier message du responsable.',
      input_schema: schemaZod(Publier, profondeur),
    },
  ]
}

// ─── Vérification ────────────────────────────────────────────────────────────

/**
 * Vérifie une valeur contre le sous-ensemble de JSON Schema que le dérouleur
 * produit. Sert aux tests et au serveur, qui refuse un appel d'outil mal formé
 * avant même de déranger Zod. Ce n'est pas un valideur général.
 */
export function verifierContreSchema(schema: SchemaJson, valeur: unknown, chemin = ''): string[] {
  const erreurs: string[] = []
  const ou = chemin || 'la racine'

  const branches = schema.anyOf ?? schema.oneOf
  if (Array.isArray(branches)) {
    const passe = branches.some((b) => verifierContreSchema(b as SchemaJson, valeur, chemin).length === 0)
    if (!passe) erreurs.push(`${ou} ne correspond à aucune forme acceptée`)
    return erreurs
  }
  if (Array.isArray(schema.allOf)) {
    for (const b of schema.allOf) erreurs.push(...verifierContreSchema(b as SchemaJson, valeur, chemin))
    return erreurs
  }
  if (Array.isArray(schema.enum) && !schema.enum.some((v) => v === valeur)) {
    erreurs.push(`${ou} n’est pas une valeur permise`)
  }
  if ('const' in schema && schema.const !== valeur) erreurs.push(`${ou} doit valoir ${JSON.stringify(schema.const)}`)

  const types = schema.type === undefined ? [] : Array.isArray(schema.type) ? (schema.type as string[]) : [schema.type as string]
  if (types.length && !types.some((t) => typeCorrespond(t, valeur))) {
    erreurs.push(`${ou} devrait être ${types.join(' ou ')}`)
    return erreurs
  }

  if (typeof valeur === 'string') {
    if (typeof schema.minLength === 'number' && valeur.length < schema.minLength) erreurs.push(`${ou} est trop court`)
    if (typeof schema.maxLength === 'number' && valeur.length > schema.maxLength) erreurs.push(`${ou} est trop long`)
    if (typeof schema.pattern === 'string' && !new RegExp(schema.pattern).test(valeur)) erreurs.push(`${ou} ne respecte pas la forme attendue`)
  }
  if (typeof valeur === 'number') {
    if (typeof schema.minimum === 'number' && valeur < schema.minimum) erreurs.push(`${ou} est trop petit`)
    if (typeof schema.maximum === 'number' && valeur > schema.maximum) erreurs.push(`${ou} est trop grand`)
  }
  if (Array.isArray(valeur)) {
    if (typeof schema.minItems === 'number' && valeur.length < schema.minItems) erreurs.push(`${ou} attend au moins ${schema.minItems} élément(s)`)
    if (typeof schema.maxItems === 'number' && valeur.length > schema.maxItems) erreurs.push(`${ou} attend au plus ${schema.maxItems} élément(s)`)
    if (schema.items) {
      valeur.forEach((v, i) => erreurs.push(...verifierContreSchema(schema.items as SchemaJson, v, `${chemin}[${i}]`)))
    }
  }
  if (valeur !== null && typeof valeur === 'object' && !Array.isArray(valeur)) {
    const objet = valeur as Record<string, unknown>
    const proprietes = (schema.properties ?? {}) as SchemaJson
    for (const requis of (schema.required ?? []) as string[]) {
      if (!(requis in objet)) erreurs.push(`${chemin ? `${chemin}.` : ''}${requis} manque`)
    }
    for (const [cle, sous] of Object.entries(objet)) {
      const schemaFils = proprietes[cle] as SchemaJson | undefined
      if (!schemaFils) {
        if (schema.additionalProperties === false) erreurs.push(`${chemin ? `${chemin}.` : ''}${cle} n’existe pas dans la grammaire`)
        continue
      }
      erreurs.push(...verifierContreSchema(schemaFils, sous, `${chemin ? `${chemin}.` : ''}${cle}`))
    }
  }
  return erreurs
}

function typeCorrespond(type: string, valeur: unknown): boolean {
  switch (type) {
    case 'object':
      return valeur !== null && typeof valeur === 'object' && !Array.isArray(valeur)
    case 'array':
      return Array.isArray(valeur)
    case 'string':
      return typeof valeur === 'string'
    case 'integer':
      return typeof valeur === 'number' && Number.isInteger(valeur)
    case 'number':
      return typeof valeur === 'number'
    case 'boolean':
      return typeof valeur === 'boolean'
    case 'null':
      return valeur === null
    default:
      return true
  }
}
